/**
 * Eigenes E-Mail-Design-Format (Blöcke → table-basiertes HTML).
 * Keine Drittanbieter-Abhängigkeit — speicherbar als JSON + gerendertes HTML.
 */

export const EMAIL_DESIGN_VERSION = 1;

export const BLOCK_TYPES = [
  { type: 'header', label: 'Kopfzeile', description: 'Logo / Titel / Link' },
  { type: 'hero', label: 'Hero', description: 'Headline + CTA (+ Bild)' },
  { type: 'text', label: 'Text', description: 'Absatz mit Formatierung' },
  { type: 'image', label: 'Bild', description: 'Bild mit optionalem Link' },
  { type: 'imageText', label: 'Bild + Text', description: 'Bild und Text kombiniert' },
  { type: 'button', label: 'Button', description: 'Call-to-Action' },
  { type: 'columns', label: 'Spalten', description: '2–4 Spalten nebeneinander' },
  { type: 'callout', label: 'Hinweisbox', description: 'Karte / Banner mit CTA' },
  { type: 'divider', label: 'Trennlinie', description: 'Horizontale Linie' },
  { type: 'spacer', label: 'Abstand', description: 'Leerraum' },
  { type: 'footer', label: 'Fußzeile', description: 'Abschluss / Impressum' },
];

/** BauOS Markenrot */
export const BAUOS_ACCENT = '#E31E24';
export const BAUOS_NAVY = '#1A1C22';

function svgDataUri(svg) {
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

/** Rotes Hexagon-Markenzeichen */
const BAUOS_MARK_PATH =
  'M26 4.5L44.5 15.2V36.8L26 47.5L7.5 36.8V15.2L26 4.5Z M26 12.2L14.8 18.7V33.3L26 39.8L37.2 33.3V18.7L26 12.2Z M26 18.5L32.2 22.1V29.9L26 33.5L19.8 29.9V22.1L26 18.5Z';

/** Logo Kopfzeile (Icon + Wortmarke) */
export const BAUOS_LOGO_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="148" height="36" viewBox="0 0 148 36" fill="none">
    <g transform="translate(0, -8) scale(0.72)">
      <path d="${BAUOS_MARK_PATH}" fill="${BAUOS_ACCENT}"/>
    </g>
    <text x="42" y="24" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="800" letter-spacing="0.06em">
      <tspan fill="#111827">BAU</tspan><tspan fill="${BAUOS_ACCENT}">OS</tspan>
    </text>
  </svg>`
);

/** Logo Footer (hell auf dunkel) */
export const BAUOS_LOGO_LIGHT_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="148" height="36" viewBox="0 0 148 36" fill="none">
    <g transform="translate(0, -8) scale(0.72)">
      <path d="${BAUOS_MARK_PATH}" fill="${BAUOS_ACCENT}"/>
    </g>
    <text x="42" y="24" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="800" letter-spacing="0.06em">
      <tspan fill="#FFFFFF">BAU</tspan><tspan fill="${BAUOS_ACCENT}">OS</tspan>
    </text>
  </svg>`
);

/** Illustration für Hero (Umschlag + Karte + Check) — ohne externe URL */
export const HERO_ENVELOPE_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="320" viewBox="0 0 420 320" fill="none">
    <circle cx="58" cy="72" r="9" fill="#D1D5DB" opacity="0.55"/>
    <circle cx="362" cy="54" r="13" fill="#E5E7EB" opacity="0.75"/>
    <circle cx="388" cy="198" r="7" fill="#D1D5DB" opacity="0.45"/>
    <circle cx="48" cy="228" r="11" fill="#E5E7EB" opacity="0.55"/>
    <circle cx="330" cy="250" r="6" fill="#D1D5DB" opacity="0.4"/>
    <!-- Envelope body -->
    <path d="M78 128H342C352 128 360 136 360 146V248C360 258 352 266 342 266H78C68 266 60 258 60 248V146C60 136 68 128 78 128Z" fill="#FFFFFF" stroke="#E5E7EB" stroke-width="2"/>
    <path d="M60 146L210 230L360 146" fill="#F9FAFB" stroke="#E5E7EB" stroke-width="2"/>
    <path d="M60 248L155 188" stroke="#E5E7EB" stroke-width="2"/>
    <path d="M360 248L265 188" stroke="#E5E7EB" stroke-width="2"/>
    <!-- Card inside -->
    <rect x="148" y="78" width="124" height="148" rx="10" fill="#2A2E36" stroke="#1A1C22" stroke-width="2"/>
    <g transform="translate(184, 118) scale(0.9)">
      <path d="${BAUOS_MARK_PATH}" fill="${BAUOS_ACCENT}"/>
    </g>
    <!-- Check badge -->
    <circle cx="300" cy="92" r="30" fill="${BAUOS_ACCENT}"/>
    <path d="M286 92 L296 102 L316 80" stroke="#FFFFFF" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  </svg>`
);

function featureIconSvg(innerPaths) {
  return svgDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
      <rect width="64" height="64" rx="14" fill="#F3F4F6"/>
      ${innerPaths}
    </svg>`
  );
}

export const ICON_CLOCK_SVG = featureIconSvg(
  `<circle cx="32" cy="32" r="14" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <path d="M32 24V33L38 36" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`
);

export const ICON_CLIPBOARD_SVG = featureIconSvg(
  `<rect x="22" y="18" width="20" height="28" rx="3" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <rect x="27" y="15" width="10" height="6" rx="2" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <path d="M28 32L31 35L37 28" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`
);

export const ICON_TEAM_SVG = featureIconSvg(
  `<circle cx="32" cy="26" r="6" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <path d="M20 44C20 38.5 25 35 32 35C39 35 44 38.5 44 44" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round" fill="none"/>
   <circle cx="44" cy="28" r="4.5" stroke="${BAUOS_ACCENT}" stroke-width="2.2" fill="none"/>
   <path d="M46 36.5C49.5 37.5 52 40 52 43.5" stroke="${BAUOS_ACCENT}" stroke-width="2.2" stroke-linecap="round" fill="none"/>`
);

export const ICON_CHAT_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
    <rect width="56" height="56" rx="14" fill="#FEE2E2"/>
    <path d="M16 20C16 17.8 17.8 16 20 16H36C38.2 16 40 17.8 40 20V32C40 34.2 38.2 36 36 36H28L22 41V36H20C17.8 36 16 34.2 16 32V20Z" stroke="${BAUOS_ACCENT}" stroke-width="2.4" fill="none" stroke-linejoin="round"/>
    <circle cx="24" cy="26" r="1.8" fill="${BAUOS_ACCENT}"/>
    <circle cx="28" cy="26" r="1.8" fill="${BAUOS_ACCENT}"/>
    <circle cx="32" cy="26" r="1.8" fill="${BAUOS_ACCENT}"/>
  </svg>`
);

/** Chat-Icon im gleichen Feature-Stil wie Uhr/Clipboard (grauer Kachel-Hintergrund) */
export const ICON_CHAT_FEATURE_SVG = featureIconSvg(
  `<path d="M20 22C20 19.8 21.8 18 24 18H40C42.2 18 44 19.8 44 22V34C44 36.2 42.2 38 40 38H32L26 43V38H24C21.8 38 20 36.2 20 34V22Z" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none" stroke-linejoin="round"/>
   <circle cx="28" cy="28" r="1.8" fill="${BAUOS_ACCENT}"/>
   <circle cx="32" cy="28" r="1.8" fill="${BAUOS_ACCENT}"/>
   <circle cx="36" cy="28" r="1.8" fill="${BAUOS_ACCENT}"/>`
);

/** Modernes Hinweis-Icon (gleicher Feature-Stil wie Uhr/Clipboard) */
export const ICON_ALERT_SVG = featureIconSvg(
  `<circle cx="32" cy="32" r="14" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <path d="M32 22V34" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round"/>
   <circle cx="32" cy="40" r="1.9" fill="${BAUOS_ACCENT}"/>`
);

export const ICON_HEART_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M14 24S4 17.5 4 10.8C4 7.5 6.5 5.5 9.2 5.5C11 5.5 12.6 6.5 14 8C15.4 6.5 17 5.5 18.8 5.5C21.5 5.5 24 7.5 24 10.8C24 17.5 14 24 14 24Z" stroke="${BAUOS_ACCENT}" stroke-width="2" stroke-linejoin="round" fill="none"/>
  </svg>`
);

/** Dashboard-/Baustellen-Illustration für Erstkontakt-Hero (kein Mac/iOS-Look) */
export const HERO_SITE_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="340" viewBox="0 0 420 340" fill="none">
    <!-- Hintergrund / Baustellen-Atmosphäre -->
    <rect width="420" height="340" fill="#EEF1F4"/>
    <rect x="0" y="250" width="420" height="90" fill="#D8DEE6"/>
    <!-- Kran -->
    <rect x="48" y="70" width="10" height="180" fill="#6B7280"/>
    <rect x="48" y="70" width="110" height="8" fill="#4B5563"/>
    <rect x="148" y="70" width="6" height="55" fill="#9CA3AF"/>
    <rect x="138" y="122" width="26" height="16" rx="2" fill="${BAUOS_ACCENT}"/>
    <!-- Gebäude-Silhouette -->
    <rect x="28" y="180" width="70" height="70" fill="#9AA3AF"/>
    <rect x="38" y="192" width="14" height="14" fill="#E5E7EB"/>
    <rect x="58" y="192" width="14" height="14" fill="#E5E7EB"/>
    <rect x="38" y="214" width="14" height="14" fill="#E5E7EB"/>
    <rect x="58" y="214" width="14" height="14" fill="#E5E7EB"/>
    <rect x="90" y="160" width="55" height="90" fill="#7B8494"/>
    <rect x="100" y="172" width="12" height="12" fill="#E5E7EB"/>
    <rect x="118" y="172" width="12" height="12" fill="#E5E7EB"/>
    <rect x="100" y="192" width="12" height="12" fill="#E5E7EB"/>
    <rect x="118" y="192" width="12" height="12" fill="#E5E7EB"/>
    <!-- Desktop-Monitor (eckig, Windows-ähnlich) -->
    <rect x="168" y="78" width="220" height="158" rx="4" fill="#1A1C22" stroke="#111318" stroke-width="3"/>
    <rect x="178" y="88" width="200" height="128" fill="#F8FAFC"/>
    <!-- Titelleiste ohne Traffic-Lights -->
    <rect x="178" y="88" width="200" height="22" fill="#E5E7EB"/>
    <rect x="186" y="95" width="10" height="8" rx="1" fill="#9CA3AF"/>
    <rect x="200" y="95" width="10" height="8" rx="1" fill="#9CA3AF"/>
    <rect x="352" y="95" width="16" height="8" rx="1" fill="${BAUOS_ACCENT}"/>
    <!-- App-UI -->
    <rect x="186" y="120" width="54" height="84" rx="3" fill="#1A1C22"/>
    <rect x="194" y="130" width="28" height="6" rx="2" fill="${BAUOS_ACCENT}"/>
    <rect x="194" y="144" width="38" height="5" rx="2" fill="#4B5563"/>
    <rect x="194" y="156" width="34" height="5" rx="2" fill="#4B5563"/>
    <rect x="194" y="168" width="30" height="5" rx="2" fill="#4B5563"/>
    <rect x="194" y="180" width="36" height="5" rx="2" fill="#4B5563"/>
    <rect x="250" y="120" width="118" height="38" rx="3" fill="#FEE2E2"/>
    <rect x="258" y="130" width="70" height="7" rx="2" fill="${BAUOS_ACCENT}"/>
    <rect x="258" y="142" width="96" height="6" rx="2" fill="#FCA5A5"/>
    <rect x="250" y="166" width="56" height="38" rx="3" fill="#E5E7EB"/>
    <rect x="312" y="166" width="56" height="38" rx="3" fill="#E5E7EB"/>
    <rect x="258" y="176" width="40" height="6" rx="2" fill="#9CA3AF"/>
    <rect x="258" y="188" width="28" height="6" rx="2" fill="#D1D5DB"/>
    <rect x="320" y="176" width="40" height="6" rx="2" fill="#9CA3AF"/>
    <rect x="320" y="188" width="28" height="6" rx="2" fill="#D1D5DB"/>
    <!-- Monitor-Standfuß -->
    <rect x="258" y="236" width="40" height="10" fill="#374151"/>
    <rect x="228" y="246" width="100" height="8" rx="2" fill="#4B5563"/>
    <!-- Bauhelm -->
    <ellipse cx="140" cy="248" rx="28" ry="12" fill="#F59E0B"/>
    <path d="M112 248 C112 232 128 222 140 222 C152 222 168 232 168 248" fill="#FBBF24"/>
    <rect x="118" y="236" width="44" height="6" rx="2" fill="#D97706"/>
    <!-- Kleines Klemmbrett -->
    <rect x="78" y="236" width="36" height="46" rx="3" fill="#FFFFFF" stroke="#9CA3AF" stroke-width="2"/>
    <rect x="88" y="230" width="16" height="8" rx="2" fill="${BAUOS_ACCENT}"/>
    <rect x="86" y="248" width="20" height="3" rx="1" fill="#D1D5DB"/>
    <rect x="86" y="256" width="16" height="3" rx="1" fill="#D1D5DB"/>
    <rect x="86" y="264" width="18" height="3" rx="1" fill="#D1D5DB"/>
  </svg>`
);

export const ICON_PHONE_SVG = featureIconSvg(
  `<rect x="24" y="16" width="16" height="32" rx="3" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <circle cx="32" cy="42" r="1.8" fill="${BAUOS_ACCENT}"/>`
);

export const ICON_CART_SVG = featureIconSvg(
  `<path d="M20 20H24L28 40H44L48 26H26" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
   <circle cx="30" cy="46" r="2.4" fill="${BAUOS_ACCENT}"/>
   <circle cx="42" cy="46" r="2.4" fill="${BAUOS_ACCENT}"/>`
);

export const ICON_SPARKLE_SVG = featureIconSvg(
  `<path d="M32 18L35 27L44 30L35 33L32 42L29 33L20 30L29 27Z" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none" stroke-linejoin="round"/>`
);

export const ICON_QR_SVG = featureIconSvg(
  `<rect x="18" y="18" width="12" height="12" rx="2" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <rect x="34" y="18" width="12" height="12" rx="2" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <rect x="18" y="34" width="12" height="12" rx="2" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <rect x="38" y="38" width="6" height="6" rx="1" fill="${BAUOS_ACCENT}"/>`
);

export const ICON_DASHBOARD_SVG = featureIconSvg(
  `<rect x="16" y="18" width="32" height="22" rx="3" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none"/>
   <path d="M22 46H42" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round"/>
   <path d="M32 40V46" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round"/>
   <path d="M21 25H31" stroke="${BAUOS_ACCENT}" stroke-width="2" stroke-linecap="round"/>
   <path d="M21 31H27" stroke="${BAUOS_ACCENT}" stroke-width="2" stroke-linecap="round"/>`
);

export const ICON_IMPORT_SVG = featureIconSvg(
  `<path d="M32 18V38" stroke="${BAUOS_ACCENT}" stroke-width="2.5" stroke-linecap="round"/>
   <path d="M25 31L32 38L39 31" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
   <path d="M18 40V44C18 45.1 18.9 46 20 46H44C45.1 46 46 45.1 46 44V40" stroke="${BAUOS_ACCENT}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
);

/** Hexagon-Mark als Bild (Fallback ohne Mediathek) */
export const BAUOS_MARK_SVG = svgDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 52 52" fill="none">
    <path d="${BAUOS_MARK_PATH}" fill="${BAUOS_ACCENT}"/>
  </svg>`
);

function uid() {
  return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Erlaubt http(s), mailto, relative Pfade (/…) und URLs mit {{platzhaltern}}; blockiert javascript: etc. */
export function sanitizeHref(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/[\s<>"']/.test(raw)) return '';
  if (/^(javascript|data|vbscript):/i.test(raw)) return '';
  if (/^(\{\{[a-zA-Z0-9_]+\}\}|mailto:|https?:\/\/)/i.test(raw)) return raw;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}([/?#].*)?$/i.test(raw)) return `https://${raw}`;
  return '';
}

/**
 * Plaintext → HTML: Zeilenumbrüche als <br>,
 * Markdown-Links [Text](https://…) als <a>.
 */
export function textToHtmlLines(value, linkColor = '#60a5fa') {
  const raw = String(value ?? '');
  if (!raw) return '';
  const color = linkColor || '#60a5fa';
  const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let html = '';
  let last = 0;
  let match;
  while ((match = LINK_RE.exec(raw)) !== null) {
    html += escapeHtml(raw.slice(last, match.index)).replace(/\r\n|\r|\n/g, '<br>');
    const label = escapeHtml(match[1]);
    const href = sanitizeHref(match[2]);
    if (href) {
      html +=
        `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" ` +
        `style="color:${escapeHtml(color)};text-decoration:underline;">${label}</a>`;
    } else {
      html += escapeHtml(match[0]).replace(/\r\n|\r|\n/g, '<br>');
    }
    last = match.index + match[0].length;
  }
  html += escapeHtml(raw.slice(last)).replace(/\r\n|\r|\n/g, '<br>');
  return html;
}

/**
 * Nur Markdown-Links [Text](url) in HTML/Text ersetzen — umgebendes HTML bleibt erhalten.
 * Nötig, wenn Platzhalter in Quill-HTML zu Markdown expandiert werden.
 */
export function expandMarkdownLinksInHtml(html, linkColor = '#2563eb') {
  const color = linkColor || '#2563eb';
  const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  return String(html ?? '').replace(LINK_RE, (full, label, url) => {
    const href = sanitizeHref(url);
    if (!href) return escapeHtml(full);
    return (
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" ` +
      `style="color:${escapeHtml(color)};text-decoration:underline;">${escapeHtml(label)}</a>`
    );
  });
}

export function defaultSettings() {
  return {
    backgroundColor: '#09090b',
    contentWidth: 600,
    contentBg: '#18181b',
    fontFamily: 'Arial, Helvetica, sans-serif',
    textColor: '#f4f4f5',
    linkColor: '#60a5fa',
  };
}

/** Settings mergen; leere Farben behalten die Defaults. */
export function mergeDesignSettings(raw = {}) {
  const base = defaultSettings();
  const next = { ...base, ...(raw || {}) };
  if (!String(next.backgroundColor || '').trim()) next.backgroundColor = base.backgroundColor;
  if (!String(next.contentBg || '').trim()) next.contentBg = base.contentBg;
  if (!String(next.textColor || '').trim()) next.textColor = base.textColor;
  if (!String(next.linkColor || '').trim()) next.linkColor = base.linkColor;
  if (!String(next.fontFamily || '').trim()) next.fontFamily = base.fontFamily;
  const w = Number(next.contentWidth);
  next.contentWidth = Number.isFinite(w) && w >= 280 ? w : base.contentWidth;
  return next;
}

export function defaultProps(type) {
  switch (type) {
    case 'header':
      return {
        logoUrl: '',
        logoWidth: 28, // % der Inhaltsbreite
        title: '{{contact_company}}',
        subtitle: '',
        rightLinkLabel: '',
        rightLinkUrl: '',
        align: 'left',
        backgroundColor: '#ffffff',
        textColor: '#111827',
        titleSize: 18,
        subtitleSize: 13,
        paddingY: 24,
        paddingX: 28,
      };
    case 'hero':
      return {
        imageUrl: '',
        imageWidth: 100, // % der Bildzelle / Volle Breite
        layout: 'stacked', // stacked | split
        eyebrow: '',
        eyebrowColor: BAUOS_ACCENT,
        headline: 'Willkommen, {{contact_name}}',
        subtext: 'Wir haben etwas Spannendes für {{contact_company}}.',
        buttonLabel: 'Mehr erfahren',
        buttonUrl: 'https://',
        buttonColor: BAUOS_ACCENT,
        buttonTextColor: '#ffffff',
        buttonRadius: 999,
        align: 'left',
        backgroundColor: '#ffffff',
        textColor: '#111827',
        headlineSize: 28,
        subtextSize: 15,
        paddingY: 8,
        paddingX: 28,
        showButton: true,
      };
    case 'text':
      return {
        content:
          'Hallo {{contact_name}},\n\nvielen Dank für Ihr Interesse. Gerne stellen wir {{contact_company}} unsere Leistungen vor.',
        align: 'left',
        fontSize: 15,
        fontWeight: '400',
        textColor: '#374151',
        backgroundColor: '',
        paddingY: 8,
        paddingX: 28,
      };
    case 'image':
      return {
        imageUrl: '',
        alt: 'Bild',
        linkUrl: '',
        width: 100,
        align: 'center',
        borderRadius: 0,
        paddingY: 12,
        paddingX: 24,
      };
    case 'imageText':
      return {
        imageUrl: '',
        alt: 'Bild',
        title: 'Überschrift',
        text: 'Beschreibender Text neben oder unter dem Bild.',
        layout: 'imageLeft', // imageLeft | imageRight | imageTop | imageBottom
        imageWidth: 42, // % in split layouts (Spaltenanteil)
        imageScale: 100, // % innerhalb der Bildzelle — SVG füllt max. 100%
        backgroundColor: '#ffffff',
        textColor: '#111827',
        mutedColor: '#6B7280',
        titleSize: 18,
        textSize: 14,
        align: 'left',
        borderRadius: 8,
        paddingY: 16,
        paddingX: 28,
        gap: 16,
        buttonLabel: '',
        buttonUrl: '',
        buttonColor: BAUOS_ACCENT,
        buttonTextColor: '#ffffff',
      };
    case 'button':
      return {
        label: 'Jetzt anfragen',
        url: 'https://',
        backgroundColor: BAUOS_ACCENT,
        textColor: '#ffffff',
        align: 'center',
        borderRadius: 999,
        fontSize: 14,
        paddingY: 16,
        buttonPaddingY: 14,
        buttonPaddingX: 28,
        fullWidth: false,
      };
    case 'columns':
      return {
        columnCount: 2,
        items: [
          { label: '', title: 'Vorteil 1', text: 'Kurze Beschreibung für die erste Spalte.' },
          { label: '', title: 'Vorteil 2', text: 'Kurze Beschreibung für die zweite Spalte.' },
        ],
        imageSize: 40, // % der Spaltenbreite — SVG füllt diesen Anteil
        backgroundColor: '#ffffff',
        textColor: '#111827',
        labelColor: '#9CA3AF',
        align: 'left',
        titleSize: 16,
        textSize: 14,
        gap: 8,
        paddingY: 16,
        paddingX: 16,
        bordered: false,
        borderColor: '#E5E7EB',
        borderRadius: 12,
      };
    case 'callout':
      return {
        title: 'Hinweis',
        text: 'Zusätzliche Information für den Empfänger.',
        align: 'left',
        backgroundColor: '#F3F4F6',
        textColor: '#111827',
        mutedColor: '#6B7280',
        borderRadius: 12,
        paddingY: 20,
        paddingX: 20,
        outerPaddingY: 8,
        outerPaddingX: 28,
        buttonLabel: '',
        buttonUrl: '',
        buttonStyle: 'solid', // solid | outline
        buttonColor: BAUOS_ACCENT,
        buttonTextColor: '#ffffff',
      };
    case 'divider':
      return {
        color: '#E5E7EB',
        thickness: 1,
        paddingY: 12,
        paddingX: 28,
      };
    case 'spacer':
      return { height: 16 };
    case 'footer':
      return {
        content:
          '© {{contact_company}}\nSie erhalten diese E-Mail, weil wir Sie kontaktieren dürfen.\n{{contact_email}}',
        align: 'center',
        backgroundColor: BAUOS_NAVY,
        textColor: '#9CA3AF',
        fontSize: 12,
        paddingY: 28,
        paddingX: 28,
      };
    default:
      return {};
  }
}

export function createBlock(type) {
  return {
    id: uid(),
    type,
    props: defaultProps(type),
  };
}

/** Spalten-Props normalisieren (inkl. Legacy left/right → items) */
export function normalizeColumnsProps(raw = {}) {
  const base = defaultProps('columns');
  const p = { ...base, ...raw };
  let items = Array.isArray(raw.items) ? raw.items.map((it) => ({
    label: it?.label != null ? String(it.label) : '',
    title: it?.title != null ? String(it.title) : '',
    text: it?.text != null ? String(it.text) : '',
    imageUrl: it?.imageUrl != null ? String(it.imageUrl) : '',
  })) : null;

  if (!items || !items.length) {
    items = [
      { label: '', title: raw.leftTitle || 'Vorteil 1', text: raw.leftText || '', imageUrl: '' },
      { label: '', title: raw.rightTitle || 'Vorteil 2', text: raw.rightText || '', imageUrl: '' },
    ];
  }

  const columnCount = Math.min(4, Math.max(2, Number(p.columnCount) || items.length || 2));
  while (items.length < columnCount) {
    items.push({ label: '', title: `Spalte ${items.length + 1}`, text: '', imageUrl: '' });
  }
  items = items.slice(0, columnCount);

  return {
    ...p,
    columnCount,
    items,
    imageSize: Math.min(100, Math.max(8, Number(p.imageSize) || 40)),
    align: p.align || 'left',
    titleSize: Number(p.titleSize) || 16,
    textSize: Number(p.textSize) || 14,
    gap: Number(p.gap) >= 0 ? Number(p.gap) : 8,
    paddingY: Number(p.paddingY) || 16,
    paddingX: Number(p.paddingX) || 16,
    labelColor: p.labelColor || '#9CA3AF',
    bordered: !!p.bordered,
    borderColor: p.borderColor || '#E5E7EB',
    borderRadius: Number(p.borderRadius) || 12,
  };
}

export function resizeColumns(props, nextCount) {
  const count = Math.min(4, Math.max(2, Number(nextCount) || 2));
  const normalized = normalizeColumnsProps({ ...props, columnCount: count });
  return { ...normalized, columnCount: count };
}

export function createEmptyDesign(seedText = '') {
  return createDefaultStyleDesign(seedText);
}

/** Neutrale Design-Vorlage für neue E-Mails (ohne Marken-Defaults) */
export function createDefaultStyleDesign(seedText = '') {
  const accent = '#0F766E';
  const navy = '#111827';
  const body =
    String(seedText || '').trim() ||
    'Hallo {{contact_anrede}},\n\nhier ist Platz für Ihren E-Mail-Text. Nutzen Sie Platzhalter wie {{contact_name}} oder {{contact_company}}.';

  return {
    version: EMAIL_DESIGN_VERSION,
    settings: {
      backgroundColor: '#F3F4F6',
      contentWidth: 640,
      contentBg: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      textColor: '#111827',
      linkColor: accent,
    },
    blocks: [
      {
        ...createBlock('header'),
        props: {
          ...defaultProps('header'),
          logoUrl: '',
          logoWidth: 32,
          title: 'Ihre Marke',
          rightLinkLabel: '',
          rightLinkUrl: '',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          titleSize: 18,
          paddingY: 22,
          paddingX: 32,
          align: 'left',
        },
      },
      {
        ...createBlock('hero'),
        props: {
          ...defaultProps('hero'),
          layout: 'stack',
          imageUrl: '',
          eyebrow: '',
          eyebrowColor: accent,
          headline: 'Betreff oder Headline',
          subtext: body,
          buttonLabel: 'Mehr erfahren',
          buttonUrl: 'https://',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          buttonRadius: 10,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          headlineSize: 28,
          subtextSize: 15,
          paddingY: 16,
          paddingX: 32,
          align: 'left',
          showButton: true,
        },
      },
      { ...createBlock('spacer'), props: { height: 16 } },
      {
        ...createBlock('footer'),
        props: {
          ...defaultProps('footer'),
          content: '{{unsubscribe_footer}}',
          align: 'center',
          backgroundColor: navy,
          textColor: '#9CA3AF',
          fontSize: 11,
          paddingY: 20,
          paddingX: 28,
        },
      },
    ],
  };
}

/**
 * BauOS Erstkontakt-Standarddesign (knallig, mit optionalen Mediathek-Logos).
 * Keep in sync with server/defaultInitialDesign.js
 */
export function createInitialOutreachDesign({ logoUrl } = {}) {
  const accent = BAUOS_ACCENT;
  const navy = BAUOS_NAVY;
  const mute = '#6B7280';
  const soft = '#F3F4F6';
  const logo = logoUrl || BAUOS_LOGO_LIGHT_SVG;

  return {
    version: EMAIL_DESIGN_VERSION,
    settings: {
      backgroundColor: '#E8EAED',
      contentWidth: 640,
      contentBg: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      textColor: '#111827',
      linkColor: accent,
    },
    blocks: [
      {
        ...createBlock('header'),
        props: {
          ...defaultProps('header'),
          logoUrl: logo,
          logoWidth: 38,
          title: '',
          subtitle: '',
          rightLinkLabel: '',
          rightLinkUrl: '',
          align: 'left',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          titleSize: 18,
          paddingY: 26,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 5, paddingY: 0, paddingX: 0 },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Software für Baustelle, Lager & Planung',
          text: 'Hardwareunabhängig auf allen Geräten · Schnell eingerichtet · Unverbindlich testen',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: navy,
          textColor: '#ffffff',
          mutedColor: '#D1D5DB',
          align: 'center',
          borderRadius: 0,
          paddingY: 18,
          paddingX: 24,
          outerPaddingY: 0,
          outerPaddingX: 0,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content:
            '{{contact_anrede}},\n\n' +
            'ich möchte Ihnen heute kurz {{company}} vorstellen – eine moderne Software für Bau- und Handwerksbetriebe, die den Arbeitsalltag auf der Baustelle und im Büro deutlich vereinfacht.\n\n' +
            'Viele Betriebe verlieren noch viel Zeit durch ständige Telefonate, Excel-Listen und Messenger-Gruppen. Wir von {{company}} haben genau dafür eine praxisnahe Software entwickelt, mit der Baustellen, Lager und Aufgabenplanung in einem System laufen – hardwareunabhängig auf allen Endgeräten nutzbar.\n\n' +
            '{{company}} ist vollständig webbasiert, läuft zuverlässig auf PC, Tablet und Smartphone und wird sicher in Deutschland gehostet.\n\n' +
            'Falls das Thema bei Ihnen gerade relevant ist, würde ich Ihnen sehr gerne in einem kurzen 15–20-minütigen Termin zeigen, wie eine Lösung bei Ihnen konkret aussehen könnte – unverbindlich und inklusive einmonatigem Testzugang.\n\n' +
            'Einfach kurz antworten oder einen passenden Termin vorschlagen.',
          align: 'left',
          fontSize: 15,
          fontWeight: '400',
          textColor: '#374151',
          paddingY: 24,
          paddingX: 32,
        },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Antwort reicht',
          text: 'Kurz zurückschreiben oder einen Termin vorschlagen – ich melde mich zeitnah. Kein Kalender-Link nötig.',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: soft,
          textColor: '#111827',
          mutedColor: mute,
          align: 'left',
          borderRadius: 12,
          paddingY: 16,
          paddingX: 22,
          outerPaddingY: 4,
          outerPaddingX: 28,
        },
      },
      {
        ...createBlock('hero'),
        props: {
          ...defaultProps('hero'),
          layout: 'split',
          imageUrl: HERO_SITE_SVG,
          imageWidth: 100,
          eyebrow: '',
          eyebrowColor: accent,
          headline: '{{company}} – Baustelle und Büro besser verbinden',
          subtext:
            '{{company}} – praxisnah für Bau- und Handwerksbetriebe, im Browser auf PC und Handy.',
          buttonLabel: '',
          buttonUrl: '',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          buttonRadius: 10,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          headlineSize: 26,
          subtextSize: 15,
          paddingY: 28,
          paddingX: 32,
          align: 'left',
          showButton: false,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content: 'Was {{company}} im Alltag verändert',
          align: 'center',
          fontSize: 20,
          fontWeight: '700',
          textColor: '#111827',
          paddingY: 20,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 3, paddingY: 0, paddingX: 260 },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: true,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 15,
          textSize: 13,
          gap: 12,
          paddingY: 20,
          paddingX: 18,
          imageSize: 40,
          items: [
            {
              label: '',
              title: 'Zeit zurückgewinnen',
              text: 'Koordination an einem Ort statt in fünf Kanälen – spürbar weniger Sucherei.',
              imageUrl: ICON_CLOCK_SVG,
            },
            {
              label: '',
              title: 'Alles im Blick',
              text: 'Baustellen, Lager und Aufgaben zentral – im Browser auf PC und Handy.',
              imageUrl: ICON_CLIPBOARD_SVG,
            },
            {
              label: '',
              title: 'Schnell startklar',
              text: 'Einrichtung meist unter einer Stunde. Unverbindlich testen, ohne Kreditkarte.',
              imageUrl: ICON_PHONE_SVG,
            },
          ],
        }),
      },
      { ...createBlock('spacer'), props: { height: 16 } },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: false,
          backgroundColor: navy,
          textColor: '#D1D5DB',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 11,
          textSize: 11,
          gap: 10,
          paddingY: 4,
          paddingX: 12,
          imageSize: 0,
          items: [
            {
              label: 'ANSCHRIFT',
              title: '',
              text: '[BauOS](https://bauos.de)\nBockenfelderstraße 232A\n44388 Dortmund',
            },
            {
              label: 'KONTAKT',
              title: '',
              text: 'Nicolai Held\nGeschäftsführer',
            },
            {
              label: 'RECHTLICHES',
              title: '',
              text: 'USt-IdNr. DE366316777\n[Impressum](https://bauos.de/impressum/)\n[Datenschutz](https://bauos.de/datenschutz/)',
            },
          ],
        }),
      },
      {
        ...createBlock('footer'),
        props: {
          ...defaultProps('footer'),
          content: '{{unsubscribe_footer}}',
          align: 'center',
          backgroundColor: navy,
          textColor: '#9CA3AF',
          fontSize: 11,
          paddingY: 10,
          paddingX: 28,
        },
      },
    ],
  };
}

/**
 * BauOS Follow-up-1-Standarddesign.
 * Keep in sync with server/defaultInitialDesign.js
 */
export function createFollowUp1OutreachDesign({ logoUrl, markUrl } = {}) {
  const accent = BAUOS_ACCENT;
  const navy = BAUOS_NAVY;
  const mute = '#6B7280';
  const soft = '#F3F4F6';
  const logo = logoUrl || BAUOS_LOGO_LIGHT_SVG;
  const mark = markUrl || BAUOS_MARK_SVG;

  return {
    version: EMAIL_DESIGN_VERSION,
    settings: {
      backgroundColor: '#E8EAED',
      contentWidth: 640,
      contentBg: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      textColor: '#111827',
      linkColor: accent,
    },
    blocks: [
      {
        ...createBlock('header'),
        props: {
          ...defaultProps('header'),
          logoUrl: logo,
          logoWidth: 38,
          title: '',
          subtitle: '',
          rightLinkLabel: '',
          rightLinkUrl: '',
          align: 'left',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          titleSize: 18,
          paddingY: 26,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 5, paddingY: 0, paddingX: 0 },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Kurzes Nachfassen',
          text: 'Passt das Thema aktuell bei Ihnen? · Unverbindlich · Mit Testzugang',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: navy,
          textColor: '#ffffff',
          mutedColor: '#D1D5DB',
          align: 'center',
          borderRadius: 0,
          paddingY: 18,
          paddingX: 24,
          outerPaddingY: 0,
          outerPaddingX: 0,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content:
            '{{contact_anrede}},\n\nich wollte nochmal kurz nachhaken zu meiner früheren E-Mail wegen {{company}}.\n\nViele Bau- und Handwerksbetriebe kämpfen noch mit zersplitterter Kommunikation zwischen Baustelle, Lager und Büro. Genau dafür haben wir {{company}} entwickelt: eine klare, übersichtliche Software, die alles an einem Ort bündelt – einfach im Browser auf Handy und PC nutzbar und sicher in Deutschland gehostet.\n\nDie Einrichtung ist meist in unter einer Stunde erledigt und bringt spürbar mehr Struktur und weniger Sucherei.\n\nFalls das bei Ihnen gerade interessant sein könnte, würde ich Ihnen sehr gerne in einem kurzen Termin zeigen, wie das konkret bei {{contact_company}} aussehen würde – unverbindlich und mit einmonatigem Testzugang.\n\nPasst es zeitlich bei Ihnen in den nächsten Tagen? Gerne auch nur eine kurze Rückmeldung.',
          align: 'left',
          fontSize: 15,
          fontWeight: '400',
          textColor: '#374151',
          paddingY: 24,
          paddingX: 32,
        },
      },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: mark,
          alt: 'BauOS',
          title: 'Einmonatiger Testzugang',
          text: 'Ohne Kreditkarte, ohne Verpflichtung – Sie sehen {{company}} direkt im Browser und entscheiden danach in Ruhe.',
          layout: 'imageLeft',
          imageWidth: 22,
          imageScale: 88,
          backgroundColor: soft,
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 10,
          paddingX: 28,
          gap: 18,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Eine kurze Antwort reicht',
          text: 'Termin vorschlagen oder kurz rückmelden – ich melde mich zeitnah. Kein Kalender-Link nötig.',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: '#FEF2F2',
          textColor: '#111827',
          mutedColor: '#7F1D1D',
          align: 'left',
          borderRadius: 12,
          paddingY: 16,
          paddingX: 22,
          outerPaddingY: 8,
          outerPaddingX: 28,
        },
      },
      {
        ...createBlock('button'),
        props: {
          ...defaultProps('button'),
          label: 'Kurz antworten',
          url: 'mailto:info@bauos.de?subject=Nochmal%20kurz%20zu%20{{company}}%20%E2%80%93%20passt%20das%20aktuell%3F',
          backgroundColor: accent,
          textColor: '#ffffff',
          align: 'center',
          borderRadius: 10,
          fontSize: 14,
          paddingY: 8,
          buttonPaddingY: 13,
          buttonPaddingX: 26,
          fullWidth: false,
        },
      },
      {
        ...createBlock('hero'),
        props: {
          ...defaultProps('hero'),
          layout: 'split',
          imageUrl: HERO_SITE_SVG,
          imageWidth: 100,
          eyebrow: '',
          eyebrowColor: accent,
          headline: '{{contact_company}} – Baustelle und Büro wieder im Griff',
          subtext:
            'Eine klare Übersicht statt Telefonaten, Excel und Messenger – unverbindlich testen.',
          buttonLabel: '',
          buttonUrl: '',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          buttonRadius: 10,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          headlineSize: 24,
          subtextSize: 15,
          paddingY: 28,
          paddingX: 32,
          align: 'left',
          showButton: false,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content: 'Was Sie mit wenig Aufwand gewinnen',
          align: 'center',
          fontSize: 20,
          fontWeight: '700',
          textColor: '#111827',
          paddingY: 20,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 3, paddingY: 0, paddingX: 260 },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: true,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 15,
          textSize: 13,
          gap: 12,
          paddingY: 20,
          paddingX: 18,
          imageSize: 40,
          items: [
            {
              label: '',
              title: 'Alles an einem Ort',
              text: 'Baustelle, Lager und Büro nutzen dieselbe zentrale Planung – immer aktuell und einheitlich.',
              imageUrl: ICON_CLIPBOARD_SVG,
            },
            {
              label: '',
              title: '+4 Stunden gespart',
              text: 'Mit {{company}} mehr Zeit und Wirtschaftlichkeit für Ihr Unternehmen.',
              imageUrl: ICON_CLOCK_SVG,
            },
            {
              label: '',
              title: 'Unverbindlich testen',
              text: 'Einmonatiger Testzugang – und bei Interesse ein kurzer Termin dazu.',
              imageUrl: ICON_PHONE_SVG,
            },
          ],
        }),
      },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: ICON_ALERT_SVG,
          alt: 'Hinweis',
          title: 'Kein langer Prozess nötig',
          text: 'Eine kurze Rückmeldung reicht völlig – ob Terminwunsch oder „aktuell nicht“. Ich melde mich dann gezielt zurück.',
          layout: 'imageRight',
          imageWidth: 24,
          imageScale: 80,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 8,
          paddingX: 28,
          gap: 16,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 2,
          bordered: true,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: soft,
          textColor: '#111827',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 14,
          textSize: 13,
          gap: 12,
          paddingY: 16,
          paddingX: 18,
          imageSize: 0,
          items: [
            {
              label: 'IM TERMIN',
              title: 'Was wir anschauen',
              text: 'Ihre Abläufe bei {{contact_company}}\nPassende {{company}}-Funktionen\nNächste Schritte inkl. Testzugang',
            },
            {
              label: 'VON IHNEN',
              title: 'Was Sie brauchen',
              text: '15–20 Minuten Zeit\nOptional 1–2 Terminvorschläge\nOder einfach eine kurze Rückmeldung',
            },
          ],
        }),
      },
      { ...createBlock('spacer'), props: { height: 16 } },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: false,
          backgroundColor: navy,
          textColor: '#D1D5DB',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 11,
          textSize: 11,
          gap: 10,
          paddingY: 4,
          paddingX: 12,
          imageSize: 0,
          items: [
            {
              label: 'ANSCHRIFT',
              title: '',
              text: '[BauOS](https://bauos.de)\nBockenfelderstraße 232A\n44388 Dortmund',
            },
            {
              label: 'KONTAKT',
              title: '',
              text: 'Nicolai Held\nGeschäftsführer',
            },
            {
              label: 'RECHTLICHES',
              title: '',
              text: 'USt-IdNr. DE366316777\n[Impressum](https://bauos.de/impressum/)\n[Datenschutz](https://bauos.de/datenschutz/)',
            },
          ],
        }),
      },
      {
        ...createBlock('footer'),
        props: {
          ...defaultProps('footer'),
          content: '{{unsubscribe_footer}}',
          align: 'center',
          backgroundColor: navy,
          textColor: '#9CA3AF',
          fontSize: 11,
          paddingY: 10,
          paddingX: 28,
        },
      },
    ],
  };
}

/**
 * BauOS Follow-up-2-Standarddesign (Testzugang).
 * Keep in sync with server/defaultInitialDesign.js
 * Layout bewusst anders als Follow-up 1 (Hero zuerst, Schritte, CTA-Callout).
 */
export function createFollowUp2OutreachDesign({ logoUrl, markUrl } = {}) {
  const accent = BAUOS_ACCENT;
  const navy = BAUOS_NAVY;
  const mute = '#6B7280';
  const soft = '#F3F4F6';
  const logo = logoUrl || BAUOS_LOGO_LIGHT_SVG;
  const mark = markUrl || BAUOS_MARK_SVG;

  return {
    version: EMAIL_DESIGN_VERSION,
    settings: {
      backgroundColor: '#EEF1F4',
      contentWidth: 640,
      contentBg: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      textColor: '#111827',
      linkColor: accent,
    },
    blocks: [
      {
        ...createBlock('header'),
        props: {
          ...defaultProps('header'),
          logoUrl: logo,
          logoWidth: 38,
          title: '',
          subtitle: '',
          rightLinkLabel: '',
          rightLinkUrl: '',
          align: 'left',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          titleSize: 18,
          paddingY: 22,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 4, paddingY: 0, paddingX: 0 },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Testzugang für {{company}}',
          text: 'Unverbindlich · Ohne Kreditkarte · Flexibel auf allen Endgeräten',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: navy,
          textColor: '#ffffff',
          mutedColor: '#D1D5DB',
          align: 'center',
          borderRadius: 0,
          paddingY: 16,
          paddingX: 24,
          outerPaddingY: 0,
          outerPaddingX: 0,
        },
      },
      {
        ...createBlock('hero'),
        props: {
          ...defaultProps('hero'),
          layout: 'split',
          imageUrl: HERO_SITE_SVG,
          imageWidth: 100,
          eyebrow: '',
          eyebrowColor: accent,
          headline: 'Einfach ausprobieren – ohne Risiko',
          subtext:
            '{{company}} richtet sich an Bau- und Handwerksbetriebe. In unter einer Stunde eingerichtet, danach direkt auf Handy und PC nutzbar.',
          buttonLabel: '',
          buttonUrl: '',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          buttonRadius: 10,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          headlineSize: 26,
          subtextSize: 15,
          paddingY: 28,
          paddingX: 32,
          align: 'left',
          showButton: false,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content:
            '{{contact_anrede}},\n\nich melde mich nochmal kurz wegen {{company}}.\n\nSie können {{company}} ganz entspannt und unverbindlich testen – ohne Kreditkarte und ohne Aufwand. Viele Betriebe merken schon nach wenigen Tagen, dass die Koordination zwischen Baustelle und Büro ruhiger und transparenter läuft.\n\nWenn Sie möchten, richte ich Ihnen gerne einen Testzugang ein und zeige in einem kurzen Termin die Funktionen, die für Ihren Betrieb am wichtigsten sind.\n\nHaben Sie in den nächsten Tagen ein paar Minuten Zeit dafür?',
          align: 'left',
          fontSize: 15,
          fontWeight: '400',
          textColor: '#374151',
          paddingY: 20,
          paddingX: 32,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content: 'So läuft der Test',
          align: 'center',
          fontSize: 20,
          fontWeight: '700',
          textColor: '#111827',
          paddingY: 12,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 3, paddingY: 0, paddingX: 260 },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: false,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: soft,
          textColor: '#111827',
          labelColor: accent,
          align: 'left',
          titleSize: 15,
          textSize: 13,
          gap: 10,
          paddingY: 18,
          paddingX: 16,
          imageSize: 36,
          items: [
            {
              label: 'SCHRITT 1',
              title: 'Kurz melden',
              text: 'Eine Antwort reicht – Testzugang oder Terminwunsch.',
              imageUrl: ICON_CHAT_FEATURE_SVG,
            },
            {
              label: 'SCHRITT 2',
              title: 'Zugang einrichten',
              text: 'In wenigen Klicks betriebsbereit.',
              imageUrl: ICON_CLOCK_SVG,
            },
            {
              label: 'SCHRITT 3',
              title: 'Im Alltag prüfen',
              text: 'Nutzen Sie {{company}} im vollen Umfang und entscheiden Sie dann, was zählt.',
              imageUrl: ICON_CLIPBOARD_SVG,
            },
          ],
        }),
      },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: mark,
          alt: 'BauOS',
          title: 'Ohne Kreditkarte, ohne Verpflichtung',
          text: 'Sie testen {{company}} live und entscheiden danach in Ruhe.',
          layout: 'imageRight',
          imageWidth: 22,
          imageScale: 88,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 12,
          paddingX: 28,
          gap: 18,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: ICON_TEAM_SVG,
          alt: 'Team',
          title: 'Nach wenigen Tagen spürbar',
          text: 'Baustelle und Büro teilen dieselbe Übersicht – weniger Bürokratie.',
          layout: 'imageLeft',
          imageWidth: 24,
          imageScale: 80,
          backgroundColor: soft,
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 10,
          paddingX: 28,
          gap: 16,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 2,
          bordered: true,
          borderColor: '#FECACA',
          borderRadius: 14,
          backgroundColor: '#FFF7F7',
          textColor: '#111827',
          labelColor: '#9F1239',
          align: 'left',
          titleSize: 14,
          textSize: 13,
          gap: 12,
          paddingY: 16,
          paddingX: 18,
          imageSize: 0,
          items: [
            {
              label: '',
              title: 'Was Sie bekommen',
              text: 'Testzugang für {{company}}\nSofort auf allen Endgeräten nutzbar',
            },
            {
              label: '',
              title: 'Dazu ein kurzer Termin',
              text: '15–20 Minuten Überblick\nFokus auf Ihre Abläufe\nNächste Schritte bei Interesse',
            },
          ],
        }),
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Testzugang gewünscht?',
          text: 'Antworten Sie einfach kurz – der Testzugang erfolgt direkt danach.',
          buttonLabel: 'Testzugang anfordern',
          buttonUrl: 'https://bauos.de/kontakt',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          backgroundColor: navy,
          textColor: '#ffffff',
          mutedColor: '#D1D5DB',
          align: 'center',
          borderRadius: 14,
          paddingY: 22,
          paddingX: 24,
          outerPaddingY: 16,
          outerPaddingX: 28,
        },
      },
      { ...createBlock('spacer'), props: { height: 12 } },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: false,
          backgroundColor: navy,
          textColor: '#D1D5DB',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 11,
          textSize: 11,
          gap: 10,
          paddingY: 4,
          paddingX: 12,
          imageSize: 0,
          items: [
            {
              label: 'ANSCHRIFT',
              title: '',
              text: '[BauOS](https://bauos.de)\nBockenfelderstraße 232A\n44388 Dortmund',
            },
            {
              label: 'KONTAKT',
              title: '',
              text: 'Nicolai Held\nGeschäftsführer',
            },
            {
              label: 'RECHTLICHES',
              title: '',
              text: 'USt-IdNr. DE366316777\n[Impressum](https://bauos.de/impressum/)\n[Datenschutz](https://bauos.de/datenschutz/)',
            },
          ],
        }),
      },
      {
        ...createBlock('footer'),
        props: {
          ...defaultProps('footer'),
          content: '{{unsubscribe_footer}}',
          align: 'center',
          backgroundColor: navy,
          textColor: '#9CA3AF',
          fontSize: 11,
          paddingY: 10,
          paddingX: 28,
        },
      },
    ],
  };
}

/**
 * BauOS Abschluss-Standarddesign (3. Sequenzmail).
 * Keep in sync with server/defaultInitialDesign.js
 * Kein „Follow-up“-Wording im sichtbaren Inhalt.
 */
export function createFollowUp3OutreachDesign({ logoUrl, markUrl } = {}) {
  const accent = BAUOS_ACCENT;
  const navy = BAUOS_NAVY;
  const mute = '#6B7280';
  const soft = '#F3F4F6';
  const logo = logoUrl || BAUOS_LOGO_LIGHT_SVG;
  const mark = markUrl || BAUOS_MARK_SVG;

  return {
    version: EMAIL_DESIGN_VERSION,
    settings: {
      backgroundColor: '#E5E7EB',
      contentWidth: 640,
      contentBg: '#ffffff',
      fontFamily: 'Arial, Helvetica, sans-serif',
      textColor: '#111827',
      linkColor: accent,
    },
    blocks: [
      {
        ...createBlock('header'),
        props: {
          ...defaultProps('header'),
          logoUrl: logo,
          logoWidth: 38,
          title: '',
          subtitle: '',
          rightLinkLabel: '',
          rightLinkUrl: '',
          align: 'left',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          titleSize: 18,
          paddingY: 22,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 4, paddingY: 0, paddingX: 0 },
      },
      {
        ...createBlock('callout'),
        props: {
          ...defaultProps('callout'),
          title: 'Zum Abschluss',
          text: 'Meine letzte Nachricht zu diesem Thema.',
          buttonLabel: '',
          buttonUrl: '',
          backgroundColor: navy,
          textColor: '#ffffff',
          mutedColor: '#D1D5DB',
          align: 'center',
          borderRadius: 0,
          paddingY: 16,
          paddingX: 24,
          outerPaddingY: 0,
          outerPaddingX: 0,
        },
      },
      {
        ...createBlock('hero'),
        props: {
          ...defaultProps('hero'),
          layout: 'split',
          imageUrl: HERO_SITE_SVG,
          imageWidth: 100,
          eyebrow: '',
          eyebrowColor: accent,
          headline: 'Viel Erfolg für {{contact_company}}',
          subtext:
            'Die Tür bleibt offen – wenn Planung und Koordination später wieder ein Thema werden.',
          buttonLabel: '',
          buttonUrl: '',
          buttonColor: accent,
          buttonTextColor: '#ffffff',
          buttonRadius: 10,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          headlineSize: 24,
          subtextSize: 15,
          paddingY: 24,
          paddingX: 32,
          align: 'left',
          showButton: false,
        },
      },
      {
        ...createBlock('text'),
        props: {
          ...defaultProps('text'),
          content: 'Was {{company}} Ihnen an Aufwand erspart',
          align: 'center',
          fontSize: 20,
          fontWeight: '700',
          textColor: '#111827',
          paddingY: 20,
          paddingX: 32,
        },
      },
      {
        ...createBlock('divider'),
        props: { color: accent, thickness: 3, paddingY: 0, paddingX: 260 },
      },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 4,
          bordered: true,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 14,
          textSize: 12,
          gap: 10,
          paddingY: 18,
          paddingX: 14,
          imageSize: 36,
          items: [
            {
              label: '',
              title: 'Einkaufsliste',
              text: 'Material- und Einkaufsbedarf jederzeit im Blick',
              imageUrl: ICON_CART_SVG,
            },
            {
              label: '',
              title: 'Baustellen-Checkliste',
              text: 'Digitaler Notizblock direkt auf der Baustelle verfügbar',
              imageUrl: ICON_CLIPBOARD_SVG,
            },
            {
              label: '',
              title: 'Reinigungsplan',
              text: 'Endreinigung der Baustelle klar geplant und dokumentiert',
              imageUrl: ICON_SPARKLE_SVG,
            },
            {
              label: '',
              title: 'Bau-Chat',
              text: 'Direkte, dokumentierte Kommunikation zwischen Büro und Baustelle',
              imageUrl: ICON_CHAT_FEATURE_SVG,
            },
          ],
        }),
      },
      { ...createBlock('spacer'), props: { height: 10 } },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 4,
          bordered: true,
          borderColor: '#E5E7EB',
          borderRadius: 14,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 14,
          textSize: 12,
          gap: 10,
          paddingY: 18,
          paddingX: 14,
          imageSize: 36,
          items: [
            {
              label: '',
              title: 'Geräteliste',
              text: 'Zuweisung an Mitarbeiter – nichts geht mehr verloren',
              imageUrl: ICON_TEAM_SVG,
            },
            {
              label: '',
              title: 'QR-Code-Scan',
              text: 'Lagermaterial scannen – im Büro und auf der Baustelle immer aktuell',
              imageUrl: ICON_QR_SVG,
            },
            {
              label: '',
              title: 'Checklisten',
              text: 'Wichtige Details direkt ins Dashboard anpinnen',
              imageUrl: ICON_DASHBOARD_SVG,
            },
            {
              label: '',
              title: 'Lagerbestand importieren',
              text: 'Gesamten Lagerbestand einfach und ohne Aufwand importieren',
              imageUrl: ICON_IMPORT_SVG,
            },
          ],
        }),
      },
      { ...createBlock('spacer'), props: { height: 16 } },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: mark,
          alt: 'BauOS',
          title: 'Noch unsicher?',
          text: 'Wir beraten Sie gerne in einem persönlichen, unverbindlichen Gespräch.',
          layout: 'imageLeft',
          imageWidth: 22,
          imageScale: 88,
          backgroundColor: soft,
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 10,
          paddingX: 28,
          gap: 18,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      {
        ...createBlock('imageText'),
        props: {
          ...defaultProps('imageText'),
          imageUrl: ICON_TEAM_SVG,
          alt: 'Kontakt',
          title: 'Bei Bedarf jederzeit erreichbar',
          text: 'Ob Testzugang oder eine konkrete Frage zu {{company}} – eine Nachricht reicht, und wir melden uns telefonisch.',
          layout: 'imageRight',
          imageWidth: 24,
          imageScale: 80,
          backgroundColor: '#ffffff',
          textColor: '#111827',
          mutedColor: mute,
          titleSize: 17,
          textSize: 14,
          align: 'left',
          borderRadius: 14,
          paddingY: 8,
          paddingX: 28,
          gap: 16,
          buttonLabel: '',
          buttonUrl: '',
        },
      },
      { ...createBlock('spacer'), props: { height: 12 } },
      {
        ...createBlock('columns'),
        props: normalizeColumnsProps({
          columnCount: 3,
          bordered: false,
          backgroundColor: navy,
          textColor: '#D1D5DB',
          labelColor: '#9CA3AF',
          align: 'left',
          titleSize: 11,
          textSize: 11,
          gap: 10,
          paddingY: 4,
          paddingX: 12,
          imageSize: 0,
          items: [
            {
              label: 'ANSCHRIFT',
              title: '',
              text: '[BauOS](https://bauos.de)\nBockenfelderstraße 232A\n44388 Dortmund',
            },
            {
              label: 'KONTAKT',
              title: '',
              text: 'Nicolai Held\nGeschäftsführer',
            },
            {
              label: 'RECHTLICHES',
              title: '',
              text: 'USt-IdNr. DE366316777\n[Impressum](https://bauos.de/impressum/)\n[Datenschutz](https://bauos.de/datenschutz/)',
            },
          ],
        }),
      },
      {
        ...createBlock('footer'),
        props: {
          ...defaultProps('footer'),
          content: '{{unsubscribe_footer}}',
          align: 'center',
          backgroundColor: navy,
          textColor: '#9CA3AF',
          fontSize: 11,
          paddingY: 10,
          paddingX: 28,
        },
      },
    ],
  };
}

export function parseDesignJson(raw) {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!data || !Array.isArray(data.blocks)) return null;
    return {
      version: data.version || EMAIL_DESIGN_VERSION,
      settings: mergeDesignSettings(data.settings || {}),
      blocks: data.blocks.map((b) => {
        const merged = { ...defaultProps(b.type), ...(b.props || {}) };
        return {
          id: b.id || uid(),
          type: b.type,
          props: b.type === 'columns' ? normalizeColumnsProps(merged) : merged,
        };
      }),
    };
  } catch {
    return null;
  }
}

export function looksLikeHtmlEmail(content) {
  const s = String(content || '').trim();
  if (!s) return false;
  if (/data-email-design\s*=\s*["']1["']/i.test(s)) return true;
  if (/^<!DOCTYPE\s+html/i.test(s) || /^<html[\s>]/i.test(s)) return true;
  return /<(table|div|p|h[1-6])\b/i.test(s) && /<\/(table|div|p|h[1-6])>/i.test(s);
}

/** Vollständiges Design-Builder-HTML (nicht Quill-Text) */
export function isDesignDocumentHtml(content) {
  const s = String(content || '').trim();
  if (!s) return false;
  if (/data-email-design\s*=\s*["']1["']/i.test(s)) return true;
  return false;
}

/**
 * Echte Design-Builder-Vorlage nur über design_json.
 * Quill-Text und übrig gebliebenes Design-HTML ohne JSON zählen nicht.
 */
export function isDesignTemplate(templateOrContent, designJson) {
  let designRaw = designJson;
  if (templateOrContent && typeof templateOrContent === 'object') {
    if (designRaw === undefined) designRaw = templateOrContent.design_json;
  }
  return !!parseDesignJson(designRaw);
}

/** Sichtbaren Text aus Design-Blöcken für den Textmodus extrahieren */
export function designToPlainText(design) {
  if (!design || !Array.isArray(design.blocks)) return '';
  const keys = [
    'title',
    'subtitle',
    'headline',
    'subtext',
    'label',
    'content',
    'leftText',
    'rightText',
    'buttonText',
  ];
  const parts = [];
  for (const block of design.blocks) {
    const props = block?.props || {};
    for (const key of keys) {
      const val = String(props[key] || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (val) parts.push(val);
    }
  }
  return parts.join('\n\n');
}

/**
 * Brieftext aus dem Design für „Als Text kopieren“:
 * bevorzugt den Textblock mit {{contact_anrede}}, plus Abmeldehinweis aus dem Footer.
 */
export function designLetterToPlainText(design) {
  if (!design || !Array.isArray(design.blocks)) return '';
  let letter = '';
  for (const block of design.blocks) {
    if (block?.type !== 'text') continue;
    const content = String(block.props?.content || '').trim();
    if (!content) continue;
    if (/\{\{\s*contact_anrede\s*\}\}/i.test(content) && content.length >= letter.length) {
      letter = content;
    }
  }
  if (!letter) return designToPlainText(design);

  let out = letter;
  if (!/\{\{\s*unsubscribe_footer\s*\}\}/i.test(out)) {
    for (const block of design.blocks) {
      if (block?.type !== 'footer') continue;
      const footer = String(block.props?.content || '');
      if (/\{\{\s*unsubscribe_footer\s*\}\}/i.test(footer)) {
        out = `${out.replace(/\s+$/, '')}\n\n{{unsubscribe_footer}}`;
        break;
      }
    }
  }
  return out;
}

function alignToCss(align) {
  if (align === 'center' || align === 'right' || align === 'left') return align;
  return 'left';
}

/**
 * Fluid image for email HTML — SVGs/Bilder füllen max. 100% ihrer Zelle.
 * @param {{ src: string, alt?: string, widthPct?: number, contentWidth?: number, borderRadius?: number, margin?: string }} opts
 */
function fluidImgHtml({
  src,
  alt = '',
  widthPct = 100,
  contentWidth = 600,
  borderRadius = 0,
  margin = '0 auto',
}) {
  const pct = Math.min(100, Math.max(5, Number(widthPct) || 100));
  const radius = Math.max(0, Number(borderRadius) || 0);
  const attrW = Math.max(16, Math.round((Number(contentWidth) || 600) * (pct / 100)));
  return (
    `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" width="${attrW}" ` +
    `style="display:block;width:${pct}%;max-width:100%;height:auto;border:0;outline:none;` +
    `border-radius:${radius}px;margin:${margin};" />`
  );
}

function renderBlock(block, settings) {
  const p = block.props || {};
  const textColor = p.textColor || settings.textColor;
  const font = settings.fontFamily;
  const linkColor = settings.linkColor || '#60a5fa';
  const lines = (value) => textToHtmlLines(value, linkColor);

  switch (block.type) {
    case 'header': {
      const contentW = Number(settings.contentWidth) || 600;
      const logoPct = Math.min(100, Math.max(8, Number(p.logoWidth) || 28));
      const logo = p.logoUrl
        ? fluidImgHtml({
            src: p.logoUrl,
            alt: '',
            widthPct: logoPct,
            contentWidth: contentW,
            margin: p.align === 'center' ? '0 auto' : p.align === 'right' ? '0 0 0 auto' : '0',
          })
        : '';
      const title = p.title
        ? `<div style="font-family:${font};font-size:${Number(p.titleSize) || 18}px;font-weight:800;letter-spacing:0.04em;color:${escapeHtml(p.textColor || '#111827')};line-height:1.2;">${lines(p.title)}</div>`
        : '';
      const subtitle = p.subtitle
        ? `<div style="font-family:${font};font-size:${Number(p.subtitleSize) || 13}px;color:${escapeHtml(p.textColor || '#111827')};opacity:0.7;margin-top:4px;">${lines(p.subtitle)}</div>`
        : '';
      const left = `${logo}${title}${subtitle}`;
      const right =
        p.rightLinkLabel
          ? `<a href="${escapeHtml(p.rightLinkUrl || '#')}" style="font-family:${font};font-size:13px;color:#6B7280;text-decoration:none;white-space:nowrap;">${escapeHtml(p.rightLinkLabel)}</a>`
          : '';
      if (right) {
        return `
        <tr>
          <td style="background:${escapeHtml(p.backgroundColor || '#ffffff')};padding:${Number(p.paddingY) || 22}px ${Number(p.paddingX) || 28}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td align="left" valign="middle">${left}</td>
                <td align="right" valign="middle">${right}</td>
              </tr>
            </table>
          </td>
        </tr>`;
      }
      return `
        <tr>
          <td align="${alignToCss(p.align)}" style="background:${escapeHtml(p.backgroundColor || '#ffffff')};padding:${Number(p.paddingY) || 22}px ${Number(p.paddingX) || 28}px;text-align:${alignToCss(p.align)};">
            ${left}
          </td>
        </tr>`;
    }
    case 'hero': {
      const contentW = Number(settings.contentWidth) || 600;
      const imgPct = Math.min(100, Math.max(10, Number(p.imageWidth) || 100));
      const showBtn = p.showButton !== false && p.buttonLabel && p.buttonUrl;
      const radius = Number(p.buttonRadius) >= 0 ? Number(p.buttonRadius) : 999;
      const btn = showBtn
        ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:18px 0 0;">
              <tr>
                <td align="center" bgcolor="${escapeHtml(p.buttonColor || BAUOS_ACCENT)}" style="border-radius:${radius}px;">
                  <a href="${escapeHtml(p.buttonUrl)}" target="_blank" style="display:inline-block;padding:14px 26px;font-family:${font};font-size:14px;font-weight:700;color:${escapeHtml(p.buttonTextColor || '#fff')};text-decoration:none;">${escapeHtml(p.buttonLabel)}</a>
                </td>
              </tr>
            </table>`
        : '';
      const eyebrow = p.eyebrow
        ? `<div style="font-family:${font};font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:${escapeHtml(p.eyebrowColor || '#F04F2F')};margin-bottom:10px;">${lines(p.eyebrow)}</div>`
        : '';
      const textCol = `
            ${eyebrow}
            ${p.headline ? `<div style="font-family:${font};font-size:${Number(p.headlineSize) || 28}px;font-weight:800;color:${escapeHtml(textColor)};line-height:1.25;margin-bottom:12px;">${lines(p.headline)}</div>` : ''}
            ${p.subtext ? `<div style="font-family:${font};font-size:${Number(p.subtextSize) || 15}px;color:#6B7280;line-height:1.55;">${lines(p.subtext)}</div>` : ''}
            ${btn}`;
      const img = p.imageUrl
        ? fluidImgHtml({
            src: p.imageUrl,
            widthPct: imgPct,
            contentWidth: Math.round(contentW * 0.42),
          })
        : '';

      if (p.layout === 'split' && p.imageUrl) {
        return `
        <tr>
          <td style="background:${escapeHtml(p.backgroundColor || '#ffffff')};padding:${Number(p.paddingY) || 12}px ${Number(p.paddingX) || 28}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td width="58%" valign="middle" style="padding-right:12px;text-align:${alignToCss(p.align)};">${textCol}</td>
                <td width="42%" valign="middle" align="center">${img}</td>
              </tr>
            </table>
          </td>
        </tr>`;
      }

      const stackedImg = p.imageUrl
        ? fluidImgHtml({
            src: p.imageUrl,
            widthPct: imgPct,
            contentWidth: contentW,
            margin: '0 auto',
          })
        : '';
      return `
        <tr>
          <td style="background:${escapeHtml(p.backgroundColor || '#ffffff')};padding:0;">
            ${stackedImg}
            <div style="padding:${Number(p.paddingY) || 24}px ${Number(p.paddingX) || 28}px;text-align:${alignToCss(p.align)};">
              ${textCol}
            </div>
          </td>
        </tr>`;
    }
    case 'text': {
      const bg = p.backgroundColor
        ? `background:${escapeHtml(p.backgroundColor)};`
        : '';
      return `
        <tr>
          <td style="${bg}padding:${Number(p.paddingY) || 8}px ${Number(p.paddingX) || 24}px;text-align:${alignToCss(p.align)};">
            <div style="font-family:${font};font-size:${Number(p.fontSize) || 15}px;font-weight:${escapeHtml(p.fontWeight || '400')};line-height:1.6;color:${escapeHtml(textColor)};">${lines(p.content)}</div>
          </td>
        </tr>`;
    }
    case 'image': {
      const contentW = Number(settings.contentWidth) || 600;
      if (!p.imageUrl) {
        return `
        <tr>
          <td align="${alignToCss(p.align)}" style="padding:${Number(p.paddingY) || 12}px ${Number(p.paddingX) || 24}px;text-align:${alignToCss(p.align)};">
            <div style="height:120px;background:#27272a;border-radius:${Number(p.borderRadius) || 0}px;"></div>
          </td>
        </tr>`;
      }
      const widthPct = Math.min(100, Math.max(5, Number(p.width) || 100));
      const radius = Number(p.borderRadius) || 0;
      const imgTag = fluidImgHtml({
        src: p.imageUrl,
        alt: p.alt || '',
        widthPct,
        contentWidth: contentW,
        borderRadius: radius,
        margin: p.align === 'left' ? '0' : p.align === 'right' ? '0 0 0 auto' : '0 auto',
      });
      const inner = p.linkUrl
        ? `<a href="${escapeHtml(p.linkUrl)}" target="_blank" style="text-decoration:none;">${imgTag}</a>`
        : imgTag;
      return `
        <tr>
          <td align="${alignToCss(p.align)}" style="padding:${Number(p.paddingY) || 12}px ${Number(p.paddingX) || 24}px;text-align:${alignToCss(p.align)};">
            ${inner}
          </td>
        </tr>`;
    }
    case 'imageText': {
      const contentW = Number(settings.contentWidth) || 600;
      const radius = Number(p.borderRadius) || 8;
      const gap = Number(p.gap) || 16;
      const imgW = Math.min(60, Math.max(14, Number(p.imageWidth) || 42));
      const textW = 100 - imgW;
      const imgScale = Math.min(100, Math.max(10, Number(p.imageScale) || 100));
      const imgHtml = p.imageUrl
        ? fluidImgHtml({
            src: p.imageUrl,
            alt: p.alt || '',
            widthPct: imgScale,
            contentWidth: Math.round(contentW * (imgW / 100)),
            borderRadius: radius,
          })
        : `<div style="height:140px;background:#E5E7EB;border-radius:${radius}px;"></div>`;
      const btn =
        p.buttonLabel && p.buttonUrl
          ? (() => {
              const btnBg = p.buttonColor || BAUOS_ACCENT;
              const isLight = /^#fff(fff)?$/i.test(String(btnBg).trim()) || String(btnBg).toLowerCase() === 'white';
              const btnRadius = Number(p.buttonRadius) >= 0 ? Number(p.buttonRadius) : 10;
              return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:14px 0 0;">
              <tr>
                <td bgcolor="${escapeHtml(btnBg)}" style="border-radius:${btnRadius}px;${isLight ? 'border:1px solid #D1D5DB;' : ''}">
                  <a href="${escapeHtml(p.buttonUrl)}" target="_blank" style="display:inline-block;padding:10px 18px;font-family:${font};font-size:13px;font-weight:700;color:${escapeHtml(p.buttonTextColor || '#fff')};text-decoration:none;">${escapeHtml(p.buttonLabel)}</a>
                </td>
              </tr>
            </table>`;
            })()
          : '';
      const textHtml = `
        ${p.title ? `<div style="font-family:${font};font-size:${Number(p.titleSize) || 18}px;font-weight:700;color:${escapeHtml(p.textColor || textColor)};line-height:1.3;margin-bottom:8px;">${lines(p.title)}</div>` : ''}
        ${p.text ? `<div style="font-family:${font};font-size:${Number(p.textSize) || 14}px;line-height:1.55;color:${escapeHtml(p.mutedColor || '#6B7280')};">${lines(p.text)}</div>` : ''}
        ${btn}`;

      const wrapInner = (inner) => `
        <tr>
          <td style="padding:${Number(p.paddingY) || 16}px ${Number(p.paddingX) || 28}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${escapeHtml(p.backgroundColor || '#ffffff')};border-radius:${radius}px;">
              <tr>
                <td style="padding:18px 16px;text-align:${alignToCss(p.align)};">
                  ${inner}
                </td>
              </tr>
            </table>
          </td>
        </tr>`;

      if (p.layout === 'imageTop') {
        return wrapInner(`<div style="margin-bottom:${gap}px;">${imgHtml}</div>${textHtml}`);
      }

      if (p.layout === 'imageBottom') {
        return wrapInner(`<div style="margin-bottom:${gap}px;">${textHtml}</div>${imgHtml}`);
      }

      const imageFirst = p.layout !== 'imageRight';
      const split = imageFirst
        ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
           <td width="${imgW}%" valign="middle" style="padding-right:${gap}px;">${imgHtml}</td>
           <td width="${textW}%" valign="middle" style="text-align:${alignToCss(p.align)};">${textHtml}</td>
           </tr></table>`
        : `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
           <td width="${textW}%" valign="middle" style="padding-right:${gap}px;text-align:${alignToCss(p.align)};">${textHtml}</td>
           <td width="${imgW}%" valign="middle">${imgHtml}</td>
           </tr></table>`;

      return wrapInner(split);
    }
    case 'button': {
      const py = Number(p.buttonPaddingY) || 12;
      const px = Number(p.buttonPaddingX) || 24;
      const btnInner = `
        <table role="presentation" cellpadding="0" cellspacing="0" ${p.fullWidth ? 'width="100%"' : `style="margin:0 ${p.align === 'left' ? '0' : 'auto'};${p.align === 'right' ? 'margin-left:auto;margin-right:0;' : p.align === 'center' ? 'margin-left:auto;margin-right:auto;' : ''}"`}>
          <tr>
            <td align="center" bgcolor="${escapeHtml(p.backgroundColor || '#3b82f6')}" style="border-radius:${Number(p.borderRadius) || 6}px;${p.fullWidth ? 'width:100%;' : ''}">
              <a href="${escapeHtml(p.url || '#')}" target="_blank" style="display:inline-block;${p.fullWidth ? 'width:100%;box-sizing:border-box;text-align:center;' : ''}padding:${py}px ${px}px;font-family:${font};font-size:${Number(p.fontSize) || 14}px;font-weight:700;color:${escapeHtml(p.textColor || '#ffffff')};text-decoration:none;">${escapeHtml(p.label || 'Button')}</a>
            </td>
          </tr>
        </table>`;
      return `
        <tr>
          <td align="${alignToCss(p.align)}" style="padding:${Number(p.paddingY) || 16}px 24px;text-align:${alignToCss(p.align)};">
            ${btnInner}
          </td>
        </tr>`;
    }
    case 'columns': {
      const cols = normalizeColumnsProps(p);
      const contentW = Number(settings.contentWidth) || 600;
      const pct = Math.floor(100 / cols.columnCount);
      const colPx = Math.round(contentW / cols.columnCount);
      const imgSize = Math.min(100, Math.max(8, Number(cols.imageSize) || 40));
      const padY = Number.isFinite(Number(cols.paddingY)) ? Number(cols.paddingY) : 16;
      const padX = Number.isFinite(Number(cols.paddingX)) ? Number(cols.paddingX) : 16;
      const outerBg = cols.backgroundColor || settings.contentBg || '#ffffff';
      const cells = cols.items
        .map(
          (item) => `
                <td width="${pct}%" valign="top" style="padding:${cols.gap}px;font-family:${font};color:${escapeHtml(cols.textColor || textColor)};text-align:${alignToCss(cols.align)};">
                  ${item.imageUrl
                    ? `<div style="margin:0 0 12px;">${fluidImgHtml({
                        src: item.imageUrl,
                        widthPct: imgSize,
                        contentWidth: colPx,
                        borderRadius: 8,
                        margin: cols.align === 'center' ? '0 auto' : cols.align === 'right' ? '0 0 0 auto' : '0',
                      })}</div>`
                    : ''}
                  ${item.label ? `<div style="font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:${escapeHtml(cols.labelColor || '#9CA3AF')};margin-bottom:4px;">${lines(item.label)}</div>` : ''}
                  ${item.title ? `<div style="font-size:${cols.titleSize}px;font-weight:700;margin-bottom:4px;">${lines(item.title)}</div>` : ''}
                  ${item.text ? `<div style="font-size:${cols.textSize}px;line-height:1.4;opacity:0.9;">${lines(item.text)}</div>` : ''}
                </td>`
        )
        .join('');
      const boxStyle = cols.bordered
        ? `border:1px solid ${escapeHtml(cols.borderColor || '#E5E7EB')};border-radius:${cols.borderRadius}px;`
        : '';
      return `
        <tr>
          <td style="background:${escapeHtml(outerBg)};padding:${padY}px ${padX}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${escapeHtml(cols.backgroundColor || '#ffffff')};${boxStyle}">
              <tr>
                ${cells}
              </tr>
            </table>
          </td>
        </tr>`;
    }
    case 'callout': {
      const hasBtn = p.buttonLabel && p.buttonUrl;
      const isOutline = p.buttonStyle === 'outline';
      const btn = hasBtn
        ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:16px ${p.align === 'center' ? 'auto' : '0'} 0;">
            <tr>
              <td align="center" ${isOutline ? '' : `bgcolor="${escapeHtml(p.buttonColor || BAUOS_ACCENT)}"`} style="border-radius:10px;${isOutline ? `border:1px solid ${escapeHtml(p.buttonColor || '#ffffff')};` : ''}">
                <a href="${escapeHtml(p.buttonUrl)}" target="_blank" style="display:inline-block;padding:12px 22px;font-family:${font};font-size:13px;font-weight:700;color:${escapeHtml(p.buttonTextColor || '#ffffff')};text-decoration:none;">${escapeHtml(p.buttonLabel)}</a>
              </td>
            </tr>
          </table>`
        : '';
      return `
        <tr>
          <td style="padding:${Number(p.outerPaddingY) || 8}px ${Number(p.outerPaddingX) || 28}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${escapeHtml(p.backgroundColor || '#F3F4F6')};border-radius:${Number(p.borderRadius) || 12}px;">
              <tr>
                <td bgcolor="${escapeHtml(p.backgroundColor || '#F3F4F6')}" style="background:${escapeHtml(p.backgroundColor || '#F3F4F6')};padding:${Number(p.paddingY) || 20}px ${Number(p.paddingX) || 20}px;text-align:${alignToCss(p.align)};color:${escapeHtml(p.textColor || '#111827')};">
                  ${p.title ? `<div style="font-family:${font};font-size:16px;font-weight:700;color:${escapeHtml(p.textColor || '#111827')};margin-bottom:6px;">${lines(p.title)}</div>` : ''}
                  ${p.text ? `<div style="font-family:${font};font-size:14px;line-height:1.55;color:${escapeHtml(p.mutedColor || '#6B7280')};">${lines(p.text)}</div>` : ''}
                  ${btn}
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
    }
    case 'divider': {
      return `
        <tr>
          <td style="padding:${Number(p.paddingY) || 12}px ${Number(p.paddingX) || 24}px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td style="border-top:${Number(p.thickness) || 1}px solid ${escapeHtml(p.color || '#3f3f46')};font-size:0;line-height:0;">&nbsp;</td>
              </tr>
            </table>
          </td>
        </tr>`;
    }
    case 'spacer': {
      return `
        <tr>
          <td style="height:${Number(p.height) || 24}px;line-height:${Number(p.height) || 24}px;font-size:0;">&nbsp;</td>
        </tr>`;
    }
    case 'footer': {
      return `
        <tr>
          <td align="${alignToCss(p.align)}" style="background:${escapeHtml(p.backgroundColor || '#09090b')};padding:${Number.isFinite(Number(p.paddingY)) ? Number(p.paddingY) : 20}px ${Number.isFinite(Number(p.paddingX)) ? Number(p.paddingX) : 24}px;text-align:${alignToCss(p.align)};">
            <div style="font-family:${font};font-size:${Number(p.fontSize) || 12}px;line-height:1.35;color:${escapeHtml(p.textColor || '#a1a1aa')};">${lines(p.content)}</div>
          </td>
        </tr>`;
    }
    default:
      return '';
  }
}

/**
 * Rendert ein vollständiges E-Mail-HTML-Dokument (Outlook-taugliche Tabellen).
 */
export function renderDesignToHtml(design) {
  const settings = mergeDesignSettings(design?.settings || {});
  const width = Number(settings.contentWidth) || 600;
  const blocks = Array.isArray(design?.blocks) ? design.blocks : [];
  const rows = blocks.map((b) => renderBlock(b, settings)).join('\n');
  const pageBg = escapeHtml(settings.backgroundColor);
  const contentBg = escapeHtml(settings.contentBg);

  return (
    `<!DOCTYPE html>` +
    `<html lang="de" data-email-design="1">` +
    `<head>` +
    `<meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<meta http-equiv="X-UA-Compatible" content="IE=edge">` +
    `<meta name="color-scheme" content="light dark">` +
    `<title>E-Mail</title>` +
    `<style>html,body{margin:0;padding:0;background:${pageBg} !important;}</style>` +
    `</head>` +
    `<body style="margin:0;padding:0;background:${pageBg};">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${pageBg};border-collapse:collapse;">` +
    `<tr><td align="center" style="padding:24px 12px;background:${pageBg};">` +
    `<table role="presentation" width="${width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${width}px;background:${contentBg};border-collapse:collapse;">` +
    `${rows}` +
    `</table>` +
    `</td></tr></table>` +
    `</body></html>`
  );
}

export function serializeDesign(design) {
  return JSON.stringify({
    version: design?.version || EMAIL_DESIGN_VERSION,
    settings: mergeDesignSettings({ ...design?.settings }),
    blocks: (design?.blocks || []).map((b) => ({
      id: b.id,
      type: b.type,
      props: b.props || {},
    })),
  });
}

export function applyPlaceholdersToHtml(html, map = {}, options = {}) {
  const highlight = !!options?.highlight;
  const source = String(html || '');

  const resolveEntry = (key) => {
    const k = String(key).toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(map, k)) {
      return { known: false, key: k };
    }
    const raw = map[k];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return {
        known: true,
        key: k,
        value: String(raw.value ?? ''),
        label: raw.label ? String(raw.label) : '',
        isContact: !!raw.isContact,
      };
    }
    return {
      known: true,
      key: k,
      value: String(raw ?? ''),
      label: '',
      isContact: false,
    };
  };

  const toHighlightMark = (entry, rawMatch) => {
    if (!entry.known) {
      return (
        `<mark title="Unbekannte Variable" ` +
        `style="border-radius:4px;padding:1px 4px;font-family:ui-monospace,monospace;font-size:0.9em;` +
        `background:#fef2f2;color:#b91c1c;">` +
        `${escapeHtml(rawMatch)}</mark>`
      );
    }
    const title = `{{${entry.key}}}${entry.label ? ` · ${entry.label}` : ''}`;
    // Inline-Styles: funktionieren auch in der Design-iframe-Vorschau (ohne Tailwind)
    const tone = entry.isContact
      ? 'background:#dbeafe;color:#1e40af;' // primary / Kontakt
      : 'background:#ffedd5;color:#9a3412;'; // accent / feste Variable
    const rendered = renderVarHtml(entry.value || '—');
    return (
      `<mark title="${escapeHtml(title)}" ` +
      `style="border-radius:4px;padding:1px 4px;font-weight:500;font-style:normal;${tone}">` +
      `${rendered}</mark>`
    );
  };

  /** Steckt der Match noch in einem offenen Attributwert (href="…{{x}}…")? */
  const getOpenAttributeName = (index) => {
    const before = source.slice(0, index);
    const lastLt = before.lastIndexOf('<');
    const lastGt = before.lastIndexOf('>');
    if (lastLt < 0 || lastLt < lastGt) return null;
    const tagPart = before.slice(lastLt);
    const m = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"[^"]*|'[^']*)$/.exec(tagPart);
    return m ? String(m[1]).toLowerCase() : null;
  };

  const valueForAttribute = (value, attrName) => {
    const raw = String(value ?? '');
    if (/^(href|src|action|poster|xlink:href)$/i.test(attrName || '')) {
      return encodeURIComponent(raw);
    }
    return escapeHtml(raw);
  };

  /** Markdown → Links; bereits HTML (z. B. Abmelde-<a>) unverändert lassen */
  const renderVarHtml = (value) => {
    const raw = String(value ?? '');
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
    return textToHtmlLines(raw, '#2563eb');
  };

  return source.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (match, key, offset) => {
    const entry = resolveEntry(key);
    const attrName = getOpenAttributeName(offset);
    if (attrName) {
      if (!entry.known) return match;
      return valueForAttribute(entry.value, attrName);
    }
    if (highlight) return toHighlightMark(entry, match);
    if (!entry.known) return match;
    return renderVarHtml(entry.value);
  });
}
